import { useState } from 'preact/hooks'
import { startAuthentication } from '@simplewebauthn/browser'
import { api, ApiError, deviceHint, type Me } from '../api.js'
import { prfAnfordern, prfAuslesen } from '../crypto/prf.js'
import { t } from '../i18n/t.js'

/**
 * Anmeldung.
 *
 * Ein großer Knopf, kein Formular. Wer einen Passkey hat, tippt einmal und ist
 * drin — kein Name, kein Passwort (Anf. 52, ADR-004). Der Passphrase-Weg ist
 * vorhanden, aber eingeklappt: Er ist der Ausnahmefall für alte Geräte, nicht
 * der Normalfall.
 */
export function Anmelden({ onDone }: {
  /** `prf` ist gesetzt, wenn der Passkey ein Geheimnis geliefert hat. */
  onDone: (me: Me, prf?: ArrayBuffer) => void
}) {
  const [busy, setBusy] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [passphraseOffen, setPassphraseOffen] = useState(false)

  async function mitPasskey() {
    setFehler(null)
    setBusy(true)
    try {
      const options = await api.post<Parameters<typeof startAuthentication>[0]['optionsJSON']>(
        '/auth/webauthn/login/options',
      )
      // Die PRF-Erweiterung wird mitgeschickt. Kann der Authenticator sie
      // nicht, kommt einfach nichts zurück — und der Speicher wird per
      // Passphrase entsperrt.
      const response = await startAuthentication({
        optionsJSON: { ...options, extensions: prfAnfordern() },
      })
      await api.post('/auth/webauthn/login/verify', { response, device: deviceHint() })
      onDone(await api.get<Me>('/me'), prfAuslesen(response.clientExtensionResults) ?? undefined)
    } catch (e) {
      setFehler(meldung(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div class="fh-anmelden">
      <h1 class="fh-h1">{t('Willkommen')}</h1>
      <p class="fh-muted">{t('Melde dich an, um deine Familie zu sehen.')}</p>

      {fehler && (
        <p class="fh-hinweis fh-hinweis--fehler" role="alert">
          <span aria-hidden="true">⚠</span> {fehler}
        </p>
      )}

      <button
        class="fh-btn fh-btn--gross"
        onClick={() => void mitPasskey()}
        disabled={busy}
      >
        <span aria-hidden="true">👆</span>
        {busy ? t('Einen Moment…') : t('Anmelden')}
      </button>

      <p class="fh-muted fh-anmelden__hilfe">
        {t('Dein Gerät fragt nach Fingerabdruck, Gesicht oder deiner Geräte-PIN.')}
      </p>

      <button
        class="fh-btn fh-btn--quiet fh-anmelden__alt"
        onClick={() => setPassphraseOffen((v) => !v)}
        aria-expanded={passphraseOffen}
      >
        {t('Anmelden mit Name und Passwort')}
      </button>

      {passphraseOffen && <MitPassphrase onDone={onDone} />}
    </div>
  )
}

function MitPassphrase({ onDone }: { onDone: (me: Me, prf?: ArrayBuffer) => void }) {
  const [name, setName] = useState('')
  const [passphrase, setPassphrase] = useState('')
  /*
   * Der zweite Schritt. Der Server antwortet mit `needsTotp`, wenn für dieses
   * Konto ein zweiter Faktor eingerichtet ist — dann wird derselbe Aufruf mit
   * dem Code wiederholt. Bis 2026-09-28 kannte die Oberfläche die Antwort
   * nicht: Sie holte danach `/me`, bekam 401 und sagte „Das hat nicht
   * geklappt" — für ein Konto, das alles richtig gemacht hatte.
   */
  const [codeGefragt, setCodeGefragt] = useState(false)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  async function absenden(e: Event) {
    e.preventDefault()
    setFehler(null)
    setBusy(true)
    try {
      const antwort = await api.post<{ needsTotp?: boolean }>('/auth/passphrase/login', {
        displayName: name, passphrase, device: deviceHint(),
        ...(codeGefragt && code ? { totp: code } : {}),
      })
      if (antwort.needsTotp) {
        setCodeGefragt(true)
        return
      }
      onDone(await api.get<Me>('/me'))
    } catch (e) {
      setFehler(meldung(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form class="fh-card fh-anmelden__formular" onSubmit={(e) => void absenden(e)}>
      <label class="fh-feld">
        <span>{t('Dein Name')}</span>
        <input
          type="text" autocomplete="username" required value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
        />
      </label>
      <label class="fh-feld">
        <span>{t('Passwort')}</span>
        <input
          type="password" autocomplete="current-password" required value={passphrase}
          onInput={(e) => setPassphrase((e.target as HTMLInputElement).value)}
        />
      </label>
      {codeGefragt && (
        <label class="fh-feld">
          <span>{t('Code aus deiner Authenticator-App')}</span>
          <input
            type="text" inputMode="numeric" autocomplete="one-time-code" pattern="[0-9]{6}"
            maxLength={6} required value={code} autofocus
            onInput={(e) => setCode((e.target as HTMLInputElement).value.replace(/\D/g, ''))}
          />
        </label>
      )}
      {fehler && <p class="fh-hinweis fh-hinweis--fehler" role="alert">{fehler}</p>}
      <button class="fh-btn" type="submit" disabled={busy}>
        {busy ? t('Einen Moment…') : t('Anmelden')}
      </button>
    </form>
  )
}

/**
 * Fehler in Alltagssprache (Anf. 59). Der Nutzer soll wissen, was er tun kann —
 * nicht, welcher Code der Server geworfen hat.
 */
function meldung(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'offline') return t('Keine Verbindung. Bist du online?')
    if (e.status === 401) return t('Das hat nicht geklappt. Versuch es noch einmal.')
    if (e.status === 429) return t('Zu viele Versuche. Warte kurz und probier es dann noch einmal.')
    return e.message
  }
  if (e instanceof Error && e.name === 'NotAllowedError') {
    return t('Abgebrochen. Tippe noch einmal auf „Anmelden".')
  }
  if (e instanceof Error && e.name === 'NotSupportedError') {
    return t('Dieses Gerät unterstützt Passkeys nicht. Nimm den Weg mit Name und Passwort.')
  }
  return t('Da ist etwas schiefgelaufen. Versuch es noch einmal.')
}
